import { format } from "date-fns";
import { useContext, useEffect, useState } from "react";
import openSocket from "../../services/socket-io";

import {
  CheckCircleOutlined,
  HeadsetMicOutlined,
  HourglassEmptyOutlined,
  TodayOutlined,
} from "@mui/icons-material";
import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useThemeContext } from "../../context/DarkMode";
import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import Chart from "./Chart";

// Surface tokens from COLOR_SPEC.md
const TILE_BG = {
  open: { light: "#E8F5EC", dark: "#1F3828" },
  pending: { light: "#E0F5F3", dark: "#1A3030" },
  closed: { light: "#EEF0EE", dark: "#2A3A2C" },
};

function greeting(h) {
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const { darkMode } = useThemeContext();

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const socket = openSocket();
    if (socket.connected) socket.emit("joinNotification");
    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on("ticket", () => setRefreshKey((k) => k + 1));
    return () => socket.disconnect();
  }, []);

  const userQueueIds = user.queues?.map((q) => q.id) ?? [];
  const queueIds = JSON.stringify(userQueueIds);

  const { count: openCount } = useTickets({
    status: "open",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
    refreshKey,
  });
  const { count: pendingCount } = useTickets({
    status: "pending",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
    refreshKey,
  });
  const { count: closedCount } = useTickets({
    status: "closed",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
    refreshKey,
  });

  const tiles = [
    {
      id: "open",
      label: i18n.t("dashboard.messages.inAttendance.title"),
      sublabel: "Active conversations",
      value: openCount,
      Icon: HeadsetMicOutlined,
      iconColor: "primary.main",
    },
    {
      id: "pending",
      label: i18n.t("dashboard.messages.waiting.title"),
      sublabel: "Queued for an agent",
      value: pendingCount,
      Icon: HourglassEmptyOutlined,
      iconColor: "secondary.main",
    },
    {
      id: "closed",
      label: i18n.t("dashboard.messages.closed.title"),
      sublabel: "Resolved today",
      value: closedCount,
      Icon: CheckCircleOutlined,
      iconColor: "text.disabled",
    },
  ];

  return (
    <Box
      sx={{ bgcolor: "background.default", minHeight: "100%", width: "100%" }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 4 }}
      >
        {/* ── Page header ── */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.5px">
            Dashboard
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 0.75 }}
          >
            <TodayOutlined sx={{ fontSize: 15, color: "text.disabled" }} />
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              {format(now, "MMMM d, yyyy")}
              {" · "}
              <Typography
                component="span"
                variant="body1"
                fontWeight={600}
                sx={{ fontFamily: "monospace", letterSpacing: "0.05em" }}
              >
                {format(now, "HH:mm:ss")}
              </Typography>
              {user.name
                ? ` · ${greeting(now.getHours())}, ${user.name.split(" ")[0]}`
                : ""}
            </Typography>
          </Stack>
        </Box>

        {/* ── Stat tiles ── */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }} columns={12}>
          {tiles.map(({ id, label, sublabel, value, Icon, iconColor }) => (
            <Grid key={id} size={{ xs: 12, sm: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  minHeight: 140,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 2,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: darkMode ? TILE_BG[id].dark : TILE_BG[id].light,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
                }}
              >
                {/* Icon + label row */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 21, color: iconColor }} />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.6px" }}
                  >
                    {label}
                  </Typography>
                </Stack>

                {/* Count + sublabel */}
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    lineHeight={1}
                    letterSpacing="-1px"
                  >
                    {value ?? "—"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {sublabel}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Chart card ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          <Chart refreshKey={refreshKey} />
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;
